import AjvDraft07, { type Options as AjvOptions } from 'ajv';
// ajv 8 ships no `exports` map, so the 2020-12 build has no top-level
// entrypoint — `ajv/dist/2020` is the import path ajv's own docs prescribe.
// eslint-disable-next-line no-restricted-imports -- ajv 2020-12 build has no other entrypoint
import Ajv2020 from 'ajv/dist/2020';

// JSON Schema dialect support for flag variation schemas. (REL-13790)
//
// MIRROR OF `internal/flags/variation_validator.go` `allowedDialectURIs` —
// keep the token↔URI map below in sync with the backend. The backend uses a
// single auto-detecting compiler (santhosh `draftFromURL`); the frontend has
// no auto-detect equivalent, so it routes between two Ajv builds (`Ajv` for
// draft-07, `Ajv2020` for 2020-12) by the schema's declared `$schema`. Both
// sides honor the same URI map.
//
// The `flag-schema-dialect` LaunchDarkly flag is a multivariate STRING flag
// whose evaluated value is a dialect spec: a comma-separated set of dialect
// tokens (e.g. `draft-07`, `2020-12`, `draft-07,2020-12`, or `` empty).
// An empty spec means dialect enforcement is OFF (any declared `$schema` is
// accepted). A non-empty spec permits only the listed dialects.

export type DialectToken = 'draft-07' | '2020-12';

// Accepted `$schema` URIs per token (trailing `#` is trimmed before lookup).
export const DIALECT_URIS: Record<DialectToken, readonly string[]> = {
  'draft-07': ['http://json-schema.org/draft-07/schema', 'https://json-schema.org/draft-07/schema'],
  '2020-12': ['https://json-schema.org/draft/2020-12/schema'],
};

const KNOWN_TOKENS: readonly DialectToken[] = ['draft-07', '2020-12'];

function isDialectToken(value: string): value is DialectToken {
  return (KNOWN_TOKENS as readonly string[]).includes(value);
}

// Reverse lookup: normalized `$schema` URI → dialect token.
const URI_TO_TOKEN: ReadonlyMap<string, DialectToken> = new Map(
  (Object.entries(DIALECT_URIS) as Array<[DialectToken, readonly string[]]>).flatMap(([token, uris]) =>
    uris.map((uri): [string, DialectToken] => [uri, token]),
  ),
);

/**
 * Parse a `flag-schema-dialect` value into the set of permitted dialect tokens.
 * Splits on `,`, trims, drops empties, and ignores unrecognized tokens. An
 * empty result means dialect enforcement is off (accept anything).
 */
export function parseDialectSpec(value: string | undefined | null): Set<DialectToken> {
  const permitted = new Set<DialectToken>();
  if (!value) {
    return permitted;
  }
  for (const part of value.split(',')) {
    const token = part.trim();
    if (isDialectToken(token)) {
      permitted.add(token);
    }
  }
  return permitted;
}

function normalizeSchemaUri(uri: string): string {
  // Drop a single trailing `#` fragment marker and surrounding whitespace; the
  // meta-schema `$id`s are published both with and without it.
  return uri.trim().replace(/#$/, '');
}

export type DeclaredDialect = DialectToken | 'implicit' | 'unknown';

/**
 * Determine the dialect a parsed schema declares via its `$schema` keyword.
 * - a known token when `$schema` maps to a recognized meta-schema URI
 * - `implicit` when no usable `$schema` is present (mirrors the backend
 *   accepting schemas with no `$schema` key regardless of policy)
 * - `unknown` when `$schema` is a string we don't recognize
 */
export function declaredDialect(parsed: unknown): DeclaredDialect {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return 'implicit';
  }
  const declared = (parsed as Record<string, unknown>).$schema;
  if (typeof declared !== 'string' || declared.trim() === '') {
    return 'implicit';
  }
  return URI_TO_TOKEN.get(normalizeSchemaUri(declared)) ?? 'unknown';
}

/**
 * Engine to use when a schema does not declare a `$schema` (decision #6):
 * validate with the newest permitted draft. 2020-12 when it is permitted or
 * when enforcement is off (empty spec); otherwise draft-07.
 */
export function effectiveDialectForImplicit(permitted: Set<DialectToken>): DialectToken {
  if (permitted.size === 0 || permitted.has('2020-12')) {
    return '2020-12';
  }
  return 'draft-07';
}

// Ajv2020 subclasses the same Ajv core, so its instances expose the
// `validateSchema` / `compile` / `errors` API the validators rely on.
export type DialectAjv = AjvDraft07;

/**
 * Build the Ajv instance that can compile the given dialect. draft-07 uses the
 * default Ajv build; 2020-12 uses the `Ajv2020` build (which registers the
 * 2020-12 meta-schema). Options are passed through so callers keep their
 * `strictSchema` / `allErrors` settings.
 */
export function makeAjvForDialect(token: DialectToken, options?: AjvOptions): DialectAjv {
  return token === '2020-12' ? new Ajv2020(options) : new AjvDraft07(options);
}

// User-facing copy for a schema whose declared `$schema` dialect this app can't
// resolve. (REL-13790)
export const UNSUPPORTED_DIALECT_MESSAGE = 'This schema declares a JSON Schema version that is not supported here.';

/**
 * Map an Ajv throw to user-facing copy. Ajv throws `no schema with key or ref
 * "..."` when a schema declares a dialect it has no meta-schema registered for;
 * that becomes UNSUPPORTED_DIALECT_MESSAGE. Any other error returns `fallback`.
 * Single source of truth for both the form validator and the variation hook.
 */
export function friendlyDialectError(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : '';
  if (/no schema with key or ref/i.test(message)) {
    return UNSUPPORTED_DIALECT_MESSAGE;
  }
  return fallback;
}
